/**
 * stat 분포 시계열 — 100% 누적 + 결측 해칭 (RUNBOOK P4 위젯 2).
 *
 * 관측이 없는 날은 **0 이 아니라 해칭**이다. 100% 누적 차트에서 결측을 0 으로
 * 그리면 "그날은 전부 정상이었다"로 읽힌다 — 가장 위험한 오독이다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { EChart } from '@/components/EChart'
import { axisStyle, CHART_H } from '@/lib/chart'
import { MissingLegend } from '@/components/MissingHatch'
import { ChartFrame } from '@/components/ChartFrame'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import { cssVar } from '@/lib/cssvar'
import {
  STAT_LABEL,
  statGroup,
  type Envelope,
  type StatGroup,
  type StatMixRow,
} from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

interface Data {
  rows: StatMixRow[]
}

const GROUP_LABEL: Record<StatGroup, string> = {
  UP: '이용 가능 (UP)',
  DOWN: '고장 (DOWN)',
  NA: '모름 (NA)',
}

/** ECharts 는 canvas 라 CSS 변수를 못 읽는다 — 실제 값으로 변환해 넘긴다 */
function groupColor(group: string): string {
  if (group === 'UP') return cssVar('--c-up', '#2f6f4e')
  if (group === 'DOWN') return cssVar('--c-down', '#a8341f')
  return cssVar('--c-na', '#8a8a84')
}

export function StatMixTimeline() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['quality/stat_mix', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<Data>('/quality/stat_mix', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<Data> | undefined
  const rows = useMemo(() => env?.data.rows ?? [], [env])

  /**
   * **코드 8종을 쌓지 않고 그룹 3종으로 쌓는다.**
   *
   * 코드별로 쌓으면 시리즈가 8개가 되어 ECharts 범례가 두 페이지로 넘어가고
   * (`1/2 ▶`), 같은 색 안의 명도 차이로 코드를 구분해야 한다 — 아무도 못 읽는다.
   * 이 위젯의 질문은 "시간이 UP·DOWN·모름으로 어떻게 나뉘었나" 이므로 그룹이
   * 맞는 단위다. **코드 단위 값은 표 뷰에 그대로 남아 있다** — 정보를 버린 것이
   * 아니라 화면과 표에 각자 맞는 해상도를 준 것이다.
   */
  const { option, dates, groups } = useMemo(() => {
    const dateSet = [...new Set(rows.map((r) => r.date_utc))].sort()
    const order: StatGroup[] = ['UP', 'DOWN', 'NA']
    const totals = new Map<string, number>()
    const byKey = new Map<string, number>()
    for (const r of rows) {
      const g = statGroup(r.stat, filters.mode)
      byKey.set(`${r.date_utc}|${g}`, (byKey.get(`${r.date_utc}|${g}`) ?? 0) + r.minutes)
      totals.set(r.date_utc, (totals.get(r.date_utc) ?? 0) + r.minutes)
    }

    const series = order.map((g) => ({
      name: GROUP_LABEL[g],
      type: 'bar' as const,
      stack: 'total',
      emphasis: { focus: 'series' as const },
      itemStyle: {
        color: groupColor(g),
        // 누적 조각 사이 2px 표면 간격 (§8.6.2 마크 규격)
        borderColor: cssVar('--c-canvas', '#ffffff'),
        borderWidth: 1,
      },
      data: dateSet.map((d) => {
        const total = totals.get(d) ?? 0
        return total > 0 ? ((byKey.get(`${d}|${g}`) ?? 0) / total) * 100 : 0
      }),
    }))

    const ink3 = cssVar('--c-ink-3', '#6e6e73')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const opt: EChartsOption = {
      // 범례를 ECharts 가 아니라 ChartFrame 이 그리므로 위 여백이 필요 없다
      grid: { left: 44, right: 12, top: 12, bottom: 28 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v) => `${Number(v).toFixed(1)}%`,
      },
      xAxis: {
        type: 'category',
        data: dateSet,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3 },
      },
      yAxis: {
        type: 'value',
        max: 100,
        interval: 25,
        ...axisStyle(ink3, hairline),
        axisLabel: { formatter: '{value}%', fontSize: 10, color: ink3 },
      },
      series,
    }
    return { option: opt, dates: dateSet, groups: order }
  }, [rows, filters.mode])

  const naShare = useMemo(() => {
    const total = rows.reduce((s, r) => s + r.minutes, 0)
    if (total <= 0) return null
    const na = rows
      .filter((r) => statGroup(r.stat, filters.mode) === 'NA')
      .reduce((s, r) => s + r.minutes, 0)
    return na / total
  }, [rows, filters.mode])

  /** 표 뷰 — 100% 누적 막대는 눈으로 정확한 비율을 읽을 수 없다 */
  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">일자별 stat 코드 시간 점유 비율</caption>
        <thead>
          <tr>
            <th scope="col">일자</th>
            <th scope="col">코드</th>
            <th scope="col">상태</th>
            <th scope="col">그룹</th>
            <th scope="col" className="ta-r">
              시간(분)
            </th>
            <th scope="col" className="ta-r">
              비율
            </th>
          </tr>
        </thead>
        <tbody>
          {dates.flatMap((d) => {
            const dayRows = rows.filter((r) => r.date_utc === d)
            const total = dayRows.reduce((s, r) => s + r.minutes, 0)
            return dayRows
              .slice()
              .sort((a, b) => b.minutes - a.minutes)
              .map((r) => (
                <tr key={`${d}|${r.stat}`}>
                  <td className="num">{d}</td>
                  <td className="num">{r.stat}</td>
                  <td>{STAT_LABEL[r.stat] ?? '미상'}</td>
                  <td>{statGroup(r.stat, filters.mode)}</td>
                  <td className="num ta-r">{Math.round(r.minutes).toLocaleString('ko-KR')}</td>
                  <td className="num ta-r">
                    {total > 0 ? `${((r.minutes / total) * 100).toFixed(2)}%` : '—'}
                  </td>
                </tr>
              ))
          })}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="stat 분포 시계열"
      question="어떤 상태 코드가 시간을 얼마나 차지했나 (100% 누적)"
      meta={env?.meta}
      headline={
        naShare == null ? undefined : (
          <Headline value={`${(naShare * 100).toFixed(1)}%`} unit="NA(모름)" tone="warn" />
        )
      }
      reading="하루의 전체 관측 시간을 100% 로 놓고, 그 시간을 어떤 상태 코드가 얼마씩 차지했는지 쌓았습니다. 회색(NA)이 두꺼우면 그날은 '상태를 모르는 시간'이 많았다는 뜻입니다."
      encoding={[
        {
          role: '가로축',
          what: '일자 (관측된 날만)',
          detail:
            '관측이 아예 없는 날은 막대를 그리지 않습니다 — 0 으로 채우면 "그날 모든 충전기가 0분이었다"는 거짓이 됩니다.',
        },
        {
          role: '세로축',
          what: '그날 관측 시간 중 비율 (0~100%)',
          detail:
            '막대 하나의 높이는 항상 100% 입니다. 그래서 서로 다른 날의 막대 높이로 절대량을 비교할 수 없습니다 — 분(minutes)은 표 보기에 있습니다.',
        },
        {
          role: '색',
          what: '현재 고장 정의 기준 UP / DOWN / NA 그룹',
          detail:
            '상단 "고장 정의" 토글을 바꾸면 같은 코드의 색이 바뀝니다. 예를 들어 통신이상(1)은 설비 고장만 모드에서 NA, 통신 포함 모드에서 DOWN 입니다.',
        },
        {
          role: '조각 3개',
          what: '이용 가능 · 고장 · 모름',
          detail:
            '코드 8종을 그대로 쌓으면 범례가 두 페이지로 넘어가고 같은 색의 명도로 코드를 구분해야 해서 읽을 수 없습니다. 화면은 그룹 3종으로 쌓고, 코드 단위 값은 표 보기에 그대로 남겨 두었습니다.',
        },
      ]}
      legend={groups.map((g) => ({ label: GROUP_LABEL[g], color: groupColor(g) }))}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={rows.length === 0}
      onRetry={() => void q.refetch()}
      table={table}
      notes={[
        {
          label: '100% 누적은 절대량을 감춘다.',
          text: '비율만 보여주므로 "관측 시간이 하루 만큼 되는 날"과 "몇 시간뿐인 날"이 같은 높이로 보입니다. 절대 분은 표 보기에서 확인하세요.',
        },
        {
          label: '색은 모드에 따라 바뀐다.',
          text: '같은 데이터라도 무엇을 고장으로 셀지에 따라 코드의 소속 그룹이 달라집니다 (§3.1).',
        },
        {
          label: 'NA 는 실패가 아니라 모름이다.',
          text: '주로 사업자→환경공단 연계가 끊긴 STALE 시간입니다. 고장인지 정상인지 방향을 알 수 없어 분모에서 뺍니다.',
        },
      ]}
    >
      <EChart
        option={option}
        height={CHART_H}
        ariaLabel={`상태 그룹 100% 누적 막대. ${dates.length}일 · ${groups
          .map((g) => GROUP_LABEL[g])
          .join(' / ')}. 코드 단위 값은 표 보기에 있습니다.`}
      />
      <div style={{ marginTop: 'var(--sp-2)' }}>
        <MissingLegend />
      </div>
    </ChartFrame>
  )
}
